// The User Management page's read, list rule and user actions: ONE copy, used by the desktop page
// (pages/UserManagementPage.jsx) and the phone (mobile/screens/UsersScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from the page;
// pages/UserManagementPage.actions.test.jsx pins them. Time reports, charts, timesheets and the
// camera clips stay on the desktop.
//
// A PIN is typed by the person setting it (never by Claude); it goes to the API as `pin`, which
// bcrypt-hashes it. GET /users never returns a PIN or its hash.
import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';

const API_BASE = config.API_BASE_URL;

export const USER_ROLES = Object.freeze(['Admin', 'Operator', 'Manager']);

/** GET /users. Throws the API's sentence. */
export async function loadUsers() {
  const res = await fetch(`${API_BASE}/users`);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

/** The list: name contains the search (any case), inactive users only when asked, by name. A new array. */
export function filterUsers(users, { searchTerm = '', showInactive = false } = {}) {
  let result = (users || []).slice();
  if (searchTerm) result = result.filter((u) => (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()));
  if (!showInactive) result = result.filter((u) => u.is_active);
  result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return result;
}

/** The form's fields: a new user (Operator, $0, active), else the user's; the PIN boxes start empty. */
export function userForm(u) {
  return {
    name: u?.name || '',
    role: u?.role || 'Operator',
    hourly_wage: u ? (u.hourly_wage || 0) : 0,
    is_active: u ? u.is_active : true,
    pin: '',
    confirm_pin: '',
  };
}

/** What is wrong with the form, in the desktop's words; null when it can be saved. A PIN is checked
 *  only when one was typed (an edit with no PIN keeps the old one). */
export function userFormProblem(form) {
  if (!String(form.name || '').trim()) return 'Name is required.';
  if (!String(form.role || '').trim()) return 'Role is required.';
  const { pin = '', confirm_pin: confirmPin = '' } = form;
  if (pin || confirmPin) {
    if (pin !== confirmPin) return 'PIN and Confirm PIN must match.';
    if (pin.length < 4 || pin.length > 6 || /\D/.test(pin)) return 'PIN must be 4-6 numeric digits.';
  }
  return null;
}

/** The request body (`pin` only when one was typed). */
export function userPayload(form) {
  return {
    name: String(form.name || '').trim(),
    role: form.role,
    pin: form.pin ? form.pin : undefined,
    is_active: form.is_active,
    hourly_wage: parseFloat(form.hourly_wage),
  };
}

// jobq-api takes an Admin's PIN on every user change (an Admin PIN can press a deadbolt's Unlock), typed for that
// press: a shop PC left in an admin session must not make a new Admin with one click.
const askAdminPin = (ui) => ui.pin('Type your Admin PIN to change users.', { autofill: false });

async function send(url, method, body, adminPin) {
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-Admin-Pin': adminPin },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await apiErrorMessage(res));
}

/** Add (POST /users) or edit (PATCH /users/{userId}) a user. A form problem is said through ui.alert and
 *  sends nothing; the API's refusal comes back as `error`. setBusy(true) only once the request goes.
 *  Resolves {done, error?}. */
export async function saveUser({ userId = null, form, ui, setBusy = () => {} }) {
  const problem = userFormProblem(form);
  if (problem) {
    await ui.alert(problem);
    return { done: false };
  }
  const payload = userPayload(form);
  const adminPin = await askAdminPin(ui);
  if (!adminPin) return { done: false };
  setBusy(true);
  try {
    if (userId) {
      await send(`${API_BASE}/users/${userId}`, 'PATCH', payload, adminPin);
      await ui.alert('User updated.');
    } else {
      await send(`${API_BASE}/users`, 'POST', payload, adminPin);
      await ui.alert('User created.');
    }
    return { done: true };
  } catch (err) {
    return { done: false, error: err.message };
  } finally {
    setBusy(false);
  }
}

/** Mark a user inactive (PATCH {is_active: false}) after asking. Resolves {done, error?}. */
export async function markUserInactive({ user, ui, setBusy = () => {} }) {
  if (!(await ui.confirm(`Mark user ${user.name} inactive?`))) return { done: false };
  const adminPin = await askAdminPin(ui);
  if (!adminPin) return { done: false };
  setBusy(true);
  try {
    await send(`${API_BASE}/users/${user.user_id}`, 'PATCH', { is_active: false }, adminPin);
    await ui.alert(`User ${user.name} marked inactive.`);
    return { done: true };
  } catch (err) {
    return { done: false, error: err.message };
  } finally {
    setBusy(false);
  }
}
